import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { catalystSignOut } from '../utils/catalystSdk';
import { authApi } from '../services/api';
import {
  SESSION_IDLE_MS,
  SESSION_REFRESH_INTERVAL_MS,
  IDLE_LOGOUT_REASON
} from '../config/session';

const AuthContext = createContext(null);

// Owner role strings — anything else is treated as staff. Kept in sync with
// the matching constant in App.js (OWNER_ROLES). When a user has just
// finalized a staff SSO, we hash-redirect them to #/staff so they land on
// the staff dashboard placeholder instead of the owner-side Home (which
// queries owner-context endpoints and 403s for staff roles).
//
// 'stakeholder' (added 2026-05-04) is an owner-side READ-ONLY role for
// C-suite / board members. Routed through the same owner pages as
// owner_contact, but write controls are hidden in the UI and rejected
// at the backend by requireNotStakeholder middleware.
const OWNER_ROLES_AUTH = new Set(['owner_contact', 'accountant', 'stakeholder']);
function isStaffRole(role) {
  return !!role && !OWNER_ROLES_AUTH.has(role);
}

export function isStakeholderRole(role) {
  return role === 'stakeholder';
}

// US-stakeholder-identify (2026-05-04)
// Stakeholders share one login. We capture per-session the human's name
// and self-described role via the IdentifyMe modal. The flag lives in
// sessionStorage so it clears when the tab closes (intentional — new
// browser session = new prompt) but survives in-tab refreshes.
const STAKEHOLDER_IDENTIFIED_KEY = 'stakeholder_identified';

// 2026-05-08 — explicit signed-out intent flag.
//
// Sign-out clears the portal's alloggio_session cookie via /auth/logout, but
// the Catalyst SDK's own SSO cookie (set by Zoho's OAuth widget) survives
// because we can't terminate that session from the SPA. On a subsequent
// refresh the AuthContext used to call tryFinalizeStaffSso() which would
// detect the still-active SDK session and silently re-issue a portal session
// — pulling the user back into the dashboard.
//
// The fix: signOut() sets this localStorage flag; refresh() honours it by
// skipping the finalize attempt. The flag is cleared whenever the user
// navigates to a sign-in surface (StaffLogin / Login pages clear it on
// mount) so a deliberate re-login still works.
export const SIGNED_OUT_FLAG_KEY = 'alloggio_signed_out';

export function clearSignedOutFlag() {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(SIGNED_OUT_FLAG_KEY); } catch {}
}

function readSignedOutFlag() {
  if (typeof window === 'undefined') return false;
  try { return window.localStorage.getItem(SIGNED_OUT_FLAG_KEY) === '1'; } catch { return false; }
}

function writeSignedOutFlag() {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(SIGNED_OUT_FLAG_KEY, '1'); } catch {}
}

function readIdentifiedFlag() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STAKEHOLDER_IDENTIFIED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.name || !parsed.role) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeIdentifiedFlag(payload) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STAKEHOLDER_IDENTIFIED_KEY, JSON.stringify(payload));
  } catch {}
}

function clearIdentifiedFlag() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STAKEHOLDER_IDENTIFIED_KEY);
  } catch {}
}

// US-F03 — if /auth/me says unauthenticated, try /auth/sso/finalize once.
// The Catalyst SDK widget (app/src/pages/StaffLogin.js) drops a Catalyst
// session cookie and redirects back to the SPA; finalize() promotes it
// into a portal session. 401 from finalize just means no Catalyst session
// was present — treat as a normal logged-out state.
async function tryFinalizeStaffSso() {
  try {
    const res = await fetch('/server/auth-service/auth/sso/finalize', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    });
    if (res.status === 200) {
      const data = await res.json();
      return { outcome: 'signed_in', user: data.user };
    }
    if (res.status === 403) {
      // Read the error TYPE — a 403 is not always "no staff role". The server
      // also refuses a sign-in whose address is already an owner login
      // (OwnerAccountConflict), and collapsing both into one outcome sent the
      // person back to a blank login page with nothing explaining why.
      let type = null;
      try {
        const data = await res.json();
        type = data && data.error && data.error.type;
      } catch { /* body may be empty — fall through to the generic outcome */ }
      if (type === 'OwnerAccountConflict') return { outcome: 'owner_account_conflict' };
      return { outcome: 'no_staff_role' };
    }
    return { outcome: 'none' };
  } catch {
    return { outcome: 'none' };
  }
}

export function AuthProvider({ children }) {
  const [state, setState] = useState({ loading: true, authenticated: false, user: null, actAsOwner: null, actAsReadOnly: false, demoCohort: null });
  const [identified, setIdentifiedState] = useState(() => readIdentifiedFlag());
  const finalizeAttempted = useRef(false);

  const setIdentified = useCallback((payload) => {
    const stored = {
      name: payload.name,
      role: payload.role,
      identified_at: payload.identified_at || new Date().toISOString()
    };
    writeIdentifiedFlag(stored);
    setIdentifiedState(stored);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/server/auth-service/auth/me', { credentials: 'include' });
      const data = await res.json();
      if (res.status === 200 && data.authenticated) {
        // PRD-ASK Internal Tools Q3 (act-as-owner) — surfacing the
        // claim from /auth/me lets the ActAsOwnerBanner render without
        // an extra round-trip.
        setState({
          loading: false,
          authenticated: true,
          user: data.user,
          actAsOwner: data.act_as_owner || null,
          // CSE view-only impersonation (Sean 2026-07-21) — drives the
          // "view only" banner copy + disables the most prominent write
          // control. Backend is the real guard; this is UX only.
          actAsReadOnly: data.act_as_read_only === true
            || !!(data.act_as_owner && data.act_as_owner.read_only),
          // Board demo cohort login (2026-06-16) — present only on a switched
          // cohort session; drives the picker / banner / write-suppression.
          demoCohort: data.demo_cohort || null
        });
        return;
      }

      // 2026-05-08 — honour explicit signed-out intent.
      // If the user just hit Sign Out, do NOT auto-finalize via the
      // surviving Catalyst SDK session. They have to navigate to a
      // sign-in surface, which clears the flag.
      if (readSignedOutFlag()) {
        setState({ loading: false, authenticated: false, user: null, actAsOwner: null });
        return;
      }

      // NEVER auto-finalize a staff SSO session on an ACTIVATION route.
      //
      // finalize() runs opportunistically on any load where /auth/me says
      // unauthenticated. On an invite link that is actively harmful: a staff
      // member with a surviving Catalyst session who opens an activation link
      // gets finalize()'d, and any non-200 outcome navigates away — so clicking
      // "Activate your account" landed on the 403 owner-conflict page instead
      // of the activation form. Reported on Dev 2026-09-15.
      //
      // These routes are PublicOnly by design: the person is proving who they
      // are with a token in the URL, and whatever session the browser happens to
      // be carrying is irrelevant to that. Same shape as the signed-out-intent
      // skip above.
      const activationRoute =
        typeof window !== 'undefined'
        && /^#\/(invite|accountant\/invite)\//.test(window.location.hash || '');
      if (activationRoute) {
        setState({ loading: false, authenticated: false, user: null, actAsOwner: null });
        return;
      }

      if (!finalizeAttempted.current) {
        finalizeAttempted.current = true;
        const result = await tryFinalizeStaffSso();
        if (result.outcome === 'signed_in') {
          setState({ loading: false, authenticated: true, user: result.user, actAsOwner: null });
          // Belt-and-braces: bounce freshly-signed-in staff to /staff so they
          // don't briefly render owner-side Home before App.js's RoleHome
          // re-routes them. Owner roles fall through to the existing default.
          if (typeof window !== 'undefined' && isStaffRole(result.user?.role)) {
            window.location.hash = '#/staff';
          }
          return;
        }
        if (result.outcome === 'owner_account_conflict') {
          // Navigate BEFORE flipping state, and with a hard replace.
          //
          // Setting state first makes React re-render immediately, and whatever
          // protected route the person is on answers an unauthenticated state
          // with <Navigate to="/login" replace />. That raced the hash
          // assignment and won: the refused staff member landed back on the
          // login page, and only saw the 403 after manually refreshing (the
          // hash had changed, but the router had already rendered login).
          //
          // location.replace forces the browser onto the error route outright,
          // so there is nothing left to race. This is a terminal state — the
          // sign-in was refused and there is nothing to preserve — so the cost
          // of a full navigation is nil, and `replace` keeps it out of history
          // so Back does not bounce through it.
          if (typeof window !== 'undefined') {
            const { origin, pathname } = window.location;
            window.location.replace(`${origin}${pathname}#/error?code=owner_account_conflict`);
            return;
          }
          setState({ loading: false, authenticated: false, user: null, actAsOwner: null });
          return;
        }
        if (result.outcome === 'no_staff_role') {
          setState({ loading: false, authenticated: false, user: null, actAsOwner: null });
          if (typeof window !== 'undefined') {
            window.location.hash = '#/error?code=no_staff_role';
          }
          return;
        }
      }

      setState({ loading: false, authenticated: false, user: null, actAsOwner: null });
    } catch {
      setState({ loading: false, authenticated: false, user: null, actAsOwner: null });
    }
  }, []);

  const signOut = useCallback(async () => {
    // Show a loading state while we work — keeps `authenticated: true` so
    // ProtectedRoute doesn't insta-redirect to /login (which would cause
    // a flash, followed by the SDK's later navigation looking like a
    // refresh).
    setState((prev) => ({ ...prev, loading: true }));

    // 1. Mark explicit signed-out intent FIRST. If anything below races
    // or partial-fails, this flag still keeps refresh() from finalizing
    // a new portal session via the surviving Catalyst SDK cookie.
    writeSignedOutFlag();
    clearIdentifiedFlag();
    setIdentifiedState(null);
    finalizeAttempted.current = false;

    // 2. Clear the portal's alloggio_session cookie via the backend.
    try {
      await fetch('/server/auth-service/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {}

    // 3. Terminate the Catalyst / Zoho SSO session itself. The SDK's
    // signOut redirects the page once Zoho has cleared the session
    // — which is what we want to happen instead of a React re-render
    // bouncing the user via ProtectedRoute (that produces the flash).
    // Per Catalyst docs:
    // https://docs.catalyst.zoho.com/en/sdk/web/v4/cloud-scale/authentication/sign-out-user/
    if (typeof window !== 'undefined') {
      const redirectUrl = `${window.location.origin}/app/index.html#/login`;
      const sdkOk = await catalystSignOut(redirectUrl);
      if (sdkOk) {
        // SDK is navigating the page asynchronously — leave React state
        // alone so we don't flash /login before the SDK reload. The new
        // page load will see the signed-out flag and stay on /login.
        return;
      }
    }

    // 4. Fallback — SDK couldn't load. Manually transition the SPA.
    setState({ loading: false, authenticated: false, user: null });
    if (typeof window !== 'undefined') {
      window.location.hash = '#/login';
    }
  }, []);

  // Idle (sliding) session logout — Foundation PRD v0.3 US-F01, Sean 2026-06-03.
  //
  // Lighter than signOut(): we don't tear down the Catalyst SSO session here
  // (that full sign-out is for the deliberate "Sign Out" button). On idle
  // expiry we just clear the portal session cookie, drop React auth state,
  // and bounce to /login with a reason so the page can show the
  // "signed out due to inactivity" message. The backend token has expired by
  // this point anyway, so the next API call would 401 regardless.
  const idleSignOut = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {}
    setState({ loading: false, authenticated: false, user: null, actAsOwner: null });
    if (typeof window !== 'undefined') {
      window.location.hash = `#/login?reason=${IDLE_LOGOUT_REASON}`;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Idle-timer + activity-driven sliding refresh. Only armed while a user is
  // authenticated. Tracks real user activity (mouse / keyboard / touch /
  // scroll); any activity resets the 60-min idle timer so an ACTIVE user is
  // never logged out. A throttled ticker pings POST /auth/refresh at most once
  // per SESSION_REFRESH_INTERVAL_MS, and only if there's been activity since
  // the last ping, so the backend idle window slides forward without
  // hammering Set-Cookie on every request.
  useEffect(() => {
    if (!state.authenticated) return undefined;
    if (typeof window === 'undefined') return undefined;

    let idleTimer = null;
    let activitySinceLastPing = false;
    let lastPingAt = Date.now();

    const onIdleExpire = () => { idleSignOut(); };

    const resetIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(onIdleExpire, SESSION_IDLE_MS);
    };

    const onActivity = () => {
      activitySinceLastPing = true;
      resetIdleTimer();
    };

    const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onActivity, { passive: true })
    );

    // Throttled refresh ticker — fires on its own interval, but only actually
    // pings the backend when there's been activity since the previous ping.
    const refreshTicker = setInterval(async () => {
      if (!activitySinceLastPing) return;
      if (Date.now() - lastPingAt < SESSION_REFRESH_INTERVAL_MS) return;
      activitySinceLastPing = false;
      lastPingAt = Date.now();
      try {
        const { status } = await authApi.refresh();
        // If the backend says the session is gone (expired between pings),
        // force the idle logout immediately rather than waiting on the timer.
        if (status === 401) onIdleExpire();
      } catch {
        // Network blip — leave the idle timer running; a genuine expiry will
        // still fire onIdleExpire or 401 on the next real request.
      }
    }, SESSION_REFRESH_INTERVAL_MS);

    // Arm the timer immediately on mount of an authenticated session.
    resetIdleTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      if (idleTimer) clearTimeout(idleTimer);
      clearInterval(refreshTicker);
    };
  }, [state.authenticated, idleSignOut]);

  // Reopened-tab fix (Sean's symptom) — when a backgrounded tab is brought
  // back to the foreground, re-validate the session against /auth/me. The
  // existing refresh() returns unauthenticated if the cookie/JWT has expired,
  // which flips state.authenticated → false and ProtectedRoute bounces to
  // /login. This stops a day-old tab restoring the prior view.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [refresh]);

  // True iff a stakeholder is signed in but has not yet submitted the
  // IdentifyMe modal in this tab session.
  const needsIdentification =
    state.authenticated && isStakeholderRole(state.user?.role) && !identified;

  return (
    <AuthContext.Provider value={{
      ...state,
      refresh,
      signOut,
      identified,
      setIdentified,
      needsIdentification
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
